import { useMemo } from 'react';
import { CustomPagePhotoPresentationInfoFragment, EventPageType, PhotoAlignment, PhotoV2WithoutBlurHashFragment, WebsitePagePhotoPresentationFragment } from '@graphql/generated';

interface Page {
  id: string;
  type: EventPageType;
  photoV2?: PhotoV2WithoutBlurHashFragment | null;
  photoPresentation?: WebsitePagePhotoPresentationFragment | null;
}

type Pages = ReadonlyArray<Page>;

interface PageContainer
  extends Readonly<{
    id: string;
    mainPhotoAlignment?: PhotoAlignment | null;
    mainPhotoPresentationLayout?: CustomPagePhotoPresentationInfoFragment['mainPhotoPresentationLayout'];
    mainPhotoCropInfo?: CustomPagePhotoPresentationInfoFragment['mainPhotoCropInfo'];
  }> {}

type PageContainers = ReadonlyArray<PageContainer>;

/**
 * Used to map page photo alignment from page container
 * Custom Pages photo focus alignment and photo are stored with different services
 */
export function usePagesWithPhotoAlignment<P extends Pages, C extends PageContainers>(pages?: P | null, pageContainers?: C | null) {
  const photoAlignedPages = useMemo(() => {
    if (!pages || !pageContainers) return pages ?? [];

    return pages.map(page => {
      const customPage = pageContainers.find(container => container.id === page.id);
      const customPagePhotoAlignment = customPage?.mainPhotoAlignment;
      const customPagePhotoPresentation = customPage?.mainPhotoPresentationLayout;
      const customPagePhotoCropInfo = customPage?.mainPhotoCropInfo;

      if (page.type === EventPageType.custom) {
        if (customPagePhotoAlignment && page.photoV2) {
          const currentPhotoV2 = page.photoV2;
          // Type narrow based on __typename to properly reconstruct the union type
          if (currentPhotoV2.__typename === 'Photo') {
            page = {
              ...page,
              photoV2: {
                ...currentPhotoV2,
                layout: {
                  ...(currentPhotoV2.layout || {}),
                  alignX: customPagePhotoAlignment.x,
                  alignY: customPagePhotoAlignment.y,
                },
              },
            };
          } else if (currentPhotoV2.__typename === 'DefaultPagePhoto') {
            page = {
              ...page,
              photoV2: {
                ...currentPhotoV2,
                layout: {
                  ...(currentPhotoV2.layout || {}),
                  alignX: customPagePhotoAlignment.x,
                  alignY: customPagePhotoAlignment.y,
                },
              },
            };
          }
        }
        if (customPagePhotoPresentation && customPagePhotoCropInfo) {
          page = {
            ...page,
            photoPresentation: {
              id: customPage.id,
              presentationLayout: customPagePhotoPresentation,
              cropInfo: customPagePhotoCropInfo,
            } as WebsitePagePhotoPresentationFragment,
          };
        }
      }
      return page;
    }) as unknown as P;
  }, [pages, pageContainers]);

  return photoAlignedPages;
}
